/**
 * Token Service
 *
 * Manages JWT authentication token storage and retrieval using SecureStorageService.
 * Provides a centralized interface for token operations across the application.
 * Tokens are obfuscated for additional security.
 *
 * @example
 * ```typescript
 * // In a component or service
 * constructor(private tokenService: TokenService) {}
 *
 * // Store token after login
 * this.tokenService.setToken('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...');
 *
 * // Check if user is authenticated
 * if (this.tokenService.hasToken()) {
 *   // User is authenticated
 * }
 *
 * // Clear token on logout
 * this.tokenService.clearToken();
 * ```
 */

import { Injectable } from '@angular/core';
import { SecureStorageService } from './secure-storage.service';
import { STORAGE_KEYS } from '../constants/security.constants';

@Injectable({
  providedIn: 'root'
})
export class TokenService {

  constructor(private secureStorage: SecureStorageService) {}

  /**
   * Stores the authentication token securely with obfuscation
   *
   * @param token - JWT authentication token to store
   *
   * @example
   * ```typescript
   * this.tokenService.setToken('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...');
   * ```
   */
  setToken(token: string): void {
    // Store token with obfuscation for additional security
    this.secureStorage.setItem(STORAGE_KEYS.TOKEN, token, true);
  }

  /**
   * Retrieves the authentication token from secure storage
   *
   * @returns The stored token or null if not found
   *
   * @example
   * ```typescript
   * const token = this.tokenService.getToken();
   * if (token) {
   *   // Use token for API requests
   * }
   * ```
   */
  getToken(): string | null {
    return this.secureStorage.getItem<string>(STORAGE_KEYS.TOKEN, true);
  }

  /**
   * Removes the authentication token from secure storage
   *
   * Typically called during logout to clear the user's session.
   *
   * @example
   * ```typescript
   * this.tokenService.clearToken();
   * ```
   */
  clearToken(): void {
    this.secureStorage.removeItem(STORAGE_KEYS.TOKEN);
  }

  /**
   * Checks if an authentication token exists
   *
   * @returns True if a token exists, false otherwise
   *
   * @example
   * ```typescript
   * if (this.tokenService.hasToken()) {
   *   console.log('User is authenticated');
   * }
   * ```
   */
  hasToken(): boolean {
    const token = this.getToken();
    return token !== null && token.length > 0;
  }
}
